import { useState, useMemo } from 'react';
import LanguageModal from '../LanguageModal';
import CloseIcon from '../CloseIcon';
import StarIcon from '../StarIcon';
import Action from '../Action';
import LogOutIcon from '../LogOutIcon';
import { AboutModal } from '../AboutModal';
import HandLeftIcon from '../HandLeftIcon';
import HandRightIcon from '../HandRightIcon';
import LetterboxdIcon from '../LetterboxdIcon';
import BookAIcon from '../BookAIcon';
import ImageIcon from '../ImageIcon';
import LetterboxdModal from '../LetterboxdModal';
import ChooseAvatarModal from '../ChooseAvatarModal';
import ChangeHandleModal from '../ChangeHandleModal';
import { motion, AnimatePresence, useAnimationControls } from 'motion/react';
import {
  consentToNotificationForYearMutation,
  logOutMutation,
  updateUserPropertiesMutation,
} from '../../resources/Auth/mutations';
import useCopy from '../../util/useCopy';
import CurrentUserResource from '../../resources/Auth/CurrentUser';
import { buyMeACoffeeUrl, STICKY_BUTTON_INSET, STICKY_BUTTON_SIZE, thisOscarsYear } from '../../config/constants';
import CoffeeIcon from '../CoffeeIcon';
import { makeClassName } from '@siteless/util';
import UserIcon from '../UserIcon';
import { PopWrapper } from '../PopWrapper';
import { useRouter } from 'next/router';
import BellIcon from '../BellIcon';
import BellStrikeThroughIcon from '../BellStrikeThroughIcon';
import InfoIcon from '../InfoIcon';
import React from 'react';
import { usePresentationMode } from '../../util/usePresentationMode';
import { Tooltip } from '../Tooltip';
import { getUserWillReceiveNotificationForYear } from '../../util/getEffectiveNotificationConsent';

let StickyMenu = ({ className }: { className?: string }) => {
  let [presentationMode] = usePresentationMode();

  let copy = useCopy();
  let router = useRouter();
  let [isActive, setIsActive] = useState(false);
  // be cautious about changing this, seems to cause issues when I use the Must
  // resource, then trigger a fast refresh by making a change in the objective state
  let [currentUser] = CurrentUserResource.useLoadable();
  let [showAboutModal, setShowAboutModal] = useState(false);
  let [showLanguageModal, setShowLanguageModal] = useState(false);
  let [showChooseAvatarModal, setShowChooseAvatarModal] = useState(false);
  let [showChangeHandleModal, setShowChangeHandleModal] = useState(false);
  let [showLetterboxdModal, setShowLetterboxdModal] = useState(false);

  let nextYear = thisOscarsYear + 1;
  let consentedToNextYearNotification = currentUser
    ? getUserWillReceiveNotificationForYear(currentUser, nextYear)
    : false;

  let menuOptions = useMemo(
    () => [
      {
        Icon: UserIcon,
        onClick: () => {
          setIsActive(false);
          setTimeout(() => {
            setShowChangeHandleModal(true);
          }, 200);
        },
        text: copy.changeScreenName,
      },
      {
        Icon: ImageIcon,
        onClick: () => {
          setIsActive(false);
          setTimeout(() => {
            setShowChooseAvatarModal(true);
          }, 200);
        },
        text: copy.changeAvatar,
      },
      {
        Icon: BookAIcon,
        onClick: () => {
          setIsActive(false);
          setTimeout(() => {
            setShowLanguageModal(true);
          }, 200);
        },
        text: copy.changeLanguage,
      },
      {
        Icon: LetterboxdIcon,
        onClick: () => {
          setIsActive(false);
          setTimeout(() => {
            setShowLetterboxdModal(true);
          }, 200);
        },
        text: currentUser?.letterboxdId
          ? copy.letterboxdSettings
          : copy.menuLetterboxd,
      },
      {
        Icon: consentedToNextYearNotification
          ? BellIcon
          : BellStrikeThroughIcon,
        onClick: () => {
          consentToNotificationForYearMutation({
            consentStatus: !consentedToNextYearNotification,
            userId: currentUser?.id,
            year: nextYear,
          });
        },
        helpText: copy.ballotReminder(nextYear),
        text: consentedToNextYearNotification
          ? copy.ballotReminderOn
          : copy.ballotReminderOff,
      },
      {
        Icon: CoffeeIcon,
        onClick: () => {
          window.open(buyMeACoffeeUrl);
        },
        text: copy['Buy me a coffee'],
      },
      {
        Icon: InfoIcon,
        onClick: () => {
          setIsActive(false);
          setTimeout(() => {
            setShowAboutModal(true);
          }, 200);
        },
        text: copy.aboutThisApp,
      },
      {
        Icon: LogOutIcon,
        onClick: () => {
          logOutMutation(undefined);
        },
        text: copy.logOut,
      },
    ],
    [copy, consentedToNextYearNotification, currentUser, nextYear],
  );

  // animations

  let triggerControls = useAnimationControls();

  return (
    <>
      <ChangeHandleModal
        isOpen={showChangeHandleModal}
        onClose={() => {
          setShowChangeHandleModal(false);
        }}
        onSave={(handle: string) => {
          updateUserPropertiesMutation({
            userId: currentUser?.id,
            handle,
          });
          setShowChangeHandleModal(false);
        }}
      />
      <ChooseAvatarModal
        isOpen={showChooseAvatarModal}
        onClose={() => {
          setShowChooseAvatarModal(false);
        }}
        onChoose={(
          avatarId: // TODO: keyof avatar options
          string,
        ) => {
          updateUserPropertiesMutation({
            userId: currentUser?.id,
            avatarId,
          });
        }}
      />
      <AboutModal
        isOpen={showAboutModal}
        onClose={() => {
          setShowAboutModal(false);
        }}
      />
      <LetterboxdModal
        isOpen={showLetterboxdModal}
        onClose={() => {
          setShowLetterboxdModal(false);
        }}
      />
      <LanguageModal
        isOpen={showLanguageModal}
        onClose={() => {
          setShowLanguageModal(false);
        }}
      />
      {isActive && currentUser?.isAdmin && (
        <div className="absolute top-0 z-30 flex w-full items-center justify-center p-4">
          <Action
            className="!text-neutral-0 !bg-orange-dark"
            onClick={() => {
              router.push('/admin');
            }}
          >
            Admin
          </Action>
        </div>
      )}
      {!presentationMode && (
        <div
          className={makeClassName(
            'absolute bottom-[40px] flex items-center justify-center overflow-visible',
            currentUser?.prefersLeftHandMode ? 'right-[38px]' : 'left-[38px]',
            isActive
              ? 'z-30 opacity-100 transition-opacity delay-100'
              : '-z-10 opacity-0',
            className,
          )}
        >
          <PopWrapper>
            <div
              onClick={() => {
                updateUserPropertiesMutation({
                  userId: currentUser?.id,
                  prefersLeftHandMode: !currentUser?.prefersLeftHandMode,
                });
              }}
              className={makeClassName(
                'border-orange-dark text-orange-dark relative flex h-[48px] w-[48px] items-center justify-center rounded-full border-2 p-2',
              )}
            >
              {currentUser?.prefersLeftHandMode ? (
                <HandLeftIcon className="h-full w-full" />
              ) : (
                <HandRightIcon className="h-full w-full" />
              )}
            </div>
          </PopWrapper>
        </div>
      )}
      <div
        className={makeClassName(
          'absolute h-0 w-0 select-none overflow-visible',
          isActive ? 'z-30' : 'z-10',
          presentationMode ? 'top-0' : 'bottom-0',
          currentUser?.prefersLeftHandMode ? 'left-0' : 'right-0',
          className,
        )}
      >
        <motion.div
          animate={triggerControls}
          className={makeClassName(
            'absolute rounded-full border-4',
            presentationMode && 'right-[16px] top-[8px] h-[70px] w-[70px]',
            isActive
              ? 'border-orange-dark bg-orange-dark text-neutral-0'
              : presentationMode
                ? 'border-purple-800 bg-purple-900 text-neutral-0'
                : 'bg-neutral-0 border-neutral-200 text-orange-400',
          )}
          style={{
            ...(presentationMode
              ? undefined
              : {
                  bottom: STICKY_BUTTON_INSET,
                  width: STICKY_BUTTON_SIZE,
                  height: STICKY_BUTTON_SIZE,
                  [currentUser?.prefersLeftHandMode ? 'left' : 'right']:
                    STICKY_BUTTON_INSET,
                }),
            boxShadow: isActive
              ? 'none'
              : '0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
          }}
          onTouchStart={() => {
            triggerControls.start({
              scale: 1.15,
              transition: { type: 'spring', stiffness: 210, damping: 20 },
            });
          }}
          onTouchEnd={() => {
            triggerControls.start({
              scale: 1,
              transition: { type: 'spring', stiffness: 210, damping: 20 },
            });
          }}
          onClick={() => {
            setIsActive((last) => !last);
          }}
        >
          <AnimatePresence mode="sync" initial={false}>
            {isActive ? (
              <motion.div
                key="close"
                initial={{ opacity: 0, rotate: -135, scale: 0 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 135, scale: 0, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 210, damping: 20 }}
                className={makeClassName(
                  'absolute cursor-pointer',
                  presentationMode
                    ? 'top-[5px] left-[5px] h-[52px] w-[52px]'
                    : 'top-[11px] left-[11px] h-[60px] w-[60px]',
                )}
              >
                <CloseIcon className="h-full w-full" />
              </motion.div>
            ) : (
              <motion.div
                key="star"
                initial={{ opacity: 0, rotate: -135, scale: 0 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 135, scale: 0, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 210, damping: 20 }}
                className={makeClassName(
                  'absolute cursor-pointer',
                  presentationMode
                    ? 'top-[5px] left-[5px] h-[52px] w-[52px]'
                    : 'top-[11px] left-[11px] h-[60px] w-[60px]',
                )}
              >
                <StarIcon className="h-full w-full" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      <AnimatePresence>
        {isActive && (
          <motion.div
            key="menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
            transition={{ duration: 0.3 }}
            className={makeClassName(
              'absolute inset-0 z-20 flex flex-col justify-center bg-orange-default p-4',
              currentUser?.prefersLeftHandMode ? 'items-start' : 'items-end',
            )}
          >
            {menuOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: 0.15,
                    delay: (menuOptions.length - 1 - index) * 0.02,
                  },
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  delay: index * 0.03,
                }}
                // willChange keeps the GPU layer alive after animation ends,
                // preventing a sub-pixel icon shift when framer-motion removes inline styles
                style={{ willChange: 'transform, opacity' }}
                className="mb-4"
              >
                <Action
                  className="!text-neutral-0 !bg-orange-dark"
                  Icon={option.Icon}
                  onClick={option.onClick}
                >
                  {option.text}
                </Action>
                {option.helpText && (
                  <Tooltip
                    text={option.helpText}
                    tooltipSide="bottom"
                    tooltipAlign={
                      currentUser?.prefersLeftHandMode ? 'start' : 'end'
                    }
                    contentClassName="w-[234px] bg-white text-neutral-800 text-center"
                  >
                    <div
                      className={makeClassName(
                        'text-orange-extraDark mx-3 -mb-1 mt-1 cursor-pointer text-sm font-medium',
                        currentUser?.prefersLeftHandMode
                          ? 'text-left'
                          : 'text-right',
                      )}
                    >
                      {copy["What's this?"]}
                    </div>
                  </Tooltip>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StickyMenu;
